import React, { useEffect, useMemo, useState } from 'react';
import Swal from '../../Librarian/librarianSwal';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import { getRolesList, getEmployeeListAdmin, getErrorMessage } from '../../api/api';
import '../../Librarian/lib-page-header.css';
import '../../Librarian/BookList.css';

/**
 * Roles are seeded server-side and there is currently no create/update/delete
 * roles endpoint, so this page is read-only. It also shows how many users hold
 * each role (derived from the employee list) to make it useful for an admin.
 *
 * SUGGESTED APIs to make roles fully manageable:
 *   POST   /roles           { name }
 *   PUT    /roles/:id        { name }
 *   DELETE /roles/:id
 */
const RoleList = () => {
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r, emps] = await Promise.all([
        getRolesList(),
        getEmployeeListAdmin().catch(() => [])
      ]);
      setRoles(Array.isArray(r) ? r : []);
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const countForRole = (roleId) =>
    employees.filter((e) => String(e.role_id) === String(roleId)).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => String(r.name || r.role_name || '').toLowerCase().includes(q));
  }, [roles, search]);

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-user-shield" /></div>
          <div>
            <h3 className="lib-page-header__title">Roles</h3>
            <div className="lib-page-header__subtitle">System roles and how many users hold each</div>
          </div>
        </div>
      </div>

      <div className="book-filter-card">
        <div className="book-filter-row">
          <div className="search__input" style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              className="book-search-input"
              placeholder="Search roles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <span style={{ color: '#6b7a99', fontSize: 13, fontWeight: 600 }}>
            {filtered.length} role{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <div className="book-table-card">
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Role ID</th>
                  <th>Role Name</th>
                  <th>Users</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="book-empty">No roles found</td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const name = r.name || r.role_name || `Role #${r.id}`;
                    return (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td><strong>{name}</strong></td>
                        <td><span className="book-count book-count-blue">{countForRole(r.id)}</span></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p style={{ margin: '14px 20px 0', fontSize: 12.5, color: '#94a3b8' }}>
        <i className="fas fa-info-circle" /> Roles are managed by the backend and are read-only here. To enable
        create/edit/delete, add <code>POST/PUT/DELETE /roles</code> endpoints.
      </p>
    </div>
  );
};

export default RoleList;
